import React from 'react';
import { twJoin } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { getOptionTileStateClasses } from '@/ui/components/shared/option-tile/option-tile-styles';
import { AttributeTileDetail } from '../types/attribute-tile-detail';
import { PickerVariant } from '../types/picker-variant';
import { PickerSize } from '../types/picker-size';
import { SelectedCheckBadge } from '../selected-check-badge/selected-check-badge';

export interface AttributeTileOptionProps {
  option: AttributeTileDetail;
  isSelected: boolean;
  onSelect: () => void;
  variant?: PickerVariant;
  size?: PickerSize;
}

interface TileState {
  isSelected: boolean;
  isClassic: boolean;
  isSmall: boolean;
}

function getTileStateClassName({ isSelected, isClassic, isSmall }: Readonly<TileState>): string {
  if (!isClassic) {
    // The small tiles only appear inside the chooser's set rows, so they sit on the row's fill.
    return getOptionTileStateClasses(isSelected, { isNested: isSmall });
  }

  return twJoin(
    // The classic small tile keeps the 1.5 px selection border Figma draws for it; Tailwind has
    // no token for it.
    isSmall && isSelected ? 'border-[1.5px]' : 'border',
    isSelected
      ? 'border-neutral-100 bg-primary-5 shadow-sm'
      : 'border-neutral-50 hover:border-neutral-70',
  );
}

export function AttributeTileOption({
  option,
  isSelected,
  onSelect,
  variant = 'default',
  size = 'default',
}: Readonly<AttributeTileOptionProps>) {
  const t = useTranslations('AttributeTilePicker');
  const isSmall = size === 'small';
  const isClassic = variant === 'classic';
  const hasCheckBadge = isClassic && isSelected;
  // The chooser arm keeps its generic label; the classic layouts name the tile after its
  // option, so tiles whose caption sits outside the button can still be told apart.
  const accessibleName = variant === 'default' && !isSmall ? t('Select option') : option.name;

  return (
    <div className={twJoin(isSmall ? 'shrink-0' : 'w-1/2', 'items-center justify-center')}>
      <button
        type="button"
        onClick={onSelect}
        className={twJoin(
          'relative flex',
          // The bundle rows draw the tiles as 36 px squares, the size of the colour swatches.
          isSmall ? 'h-9 w-9 items-center justify-center rounded-lg' : 'w-full rounded-md',
          isSmall ? 'p-1.5' : 'p-6',
          getTileStateClassName({ isSelected, isClassic, isSmall }),
        )}
        aria-label={accessibleName}
        aria-pressed={isSelected}
      >
        {hasCheckBadge && <SelectedCheckBadge />}
        <OptimizedImage
          image={option.image}
          sizes="100px"
          width={isSmall ? 24 : 48}
          height={isSmall ? 24 : 48}
          className={isSmall ? 'mx-auto h-6 w-6 rounded-full' : 'mx-auto h-12 rounded-full'}
        />
      </button>
      {!isSmall && <div className="mt-2 text-center font-bold">{option.name}</div>}
    </div>
  );
}
